import React, { useState, useEffect, useContext } from "react";
import CustomFirmwarePieChart from "./CustomFirmwarePieChart";
import apiClient from '../../utils/apiClient';
import { ProfileContext } from '../../contexts/ProfileContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadialBarChart,
  RadialBar
} from "recharts";
import "./Dashboard.css";

const Dashboard = () => {
  const [accessAttempts, setAccessAttempts] = useState([]);
  const [avgBattery, setAvgBattery] = useState([]);
  const [backendErrors, setBackendErrors] = useState([]);
  const [filter, setFilter] = useState("7days");
  const [batteryFilter, setBatteryFilter] = useState("7days");
  const [errorsFilter, setErrorsFilter] = useState("7days");
  const [customAccessStart, setCustomAccessStart] = useState("");
  const [customAccessEnd, setCustomAccessEnd] = useState("");
  const [customBatteryStart, setCustomBatteryStart] = useState("");
  const [customBatteryEnd, setCustomBatteryEnd] = useState("");
  const [customErrorsStart, setCustomErrorsStart] = useState("");
  const [customErrorsEnd, setCustomErrorsEnd] = useState("");
  const [userInteractions, setUserInteractions] = useState([]);
  const [userInteractionFilter, setUserInteractionFilter] = useState("7days");
  const [firmwareData, setFirmwareData] = useState([]);
  const [firmwareFilter, setFirmwareFilter] = useState("7days");
  const [customUserStart, setCustomUserStart] = useState("");
  const [customUserEnd, setCustomUserEnd] = useState("");
  const [customFirmwareStart, setCustomFirmwareStart] = useState("");
  const [customFirmwareEnd, setCustomFirmwareEnd] = useState("");
  const { profile } = useContext(ProfileContext);

  useEffect(() => {
    const fetchFirmwareData = async () => {
      const res = await apiClient.get(`/metrics_device_firmware`);
      const formatted = Object.entries(res.data).map(([version, pct]) => ({
        name: version,
        value: parseFloat(pct.replace("%", ""))
      }));
      setFirmwareData(formatted);
    };
    fetchFirmwareData();
  }, []);

  useEffect(() => {
    const fetchAvgBattery = async () => {
      const { startDate, endDate } = getDateRange(batteryFilter, customBatteryStart, customBatteryEnd) || {};
      if (!startDate || !endDate) return;
      const groupBy = batteryFilter === "today" ? "hourly" : "daily";

      try {
        const response = await apiClient.get(`/metrics_device_battery`, {
          params: {
            from: startDate.toISOString(),
            to: endDate.toISOString(),
            group_by: groupBy
          }
        });

        const fullDateMap = generateBatteryDateRangeMap(startDate, endDate, groupBy);
        const receivedData = response.data;

        for (const [dateStr, avg] of Object.entries(receivedData)) {
          const key = groupBy === 'hourly'
            ? dateStr.slice(0, 13) + ':00:00'
            : dateStr.slice(0, 10);

          if (fullDateMap[key]) {
            fullDateMap[key].avg_battery_pct = avg;
          }
        }

        setAvgBattery(Object.values(fullDateMap));
      } catch (error) {
        console.error(error);
      }
    };

    fetchAvgBattery();
  }, [batteryFilter, customBatteryStart, customBatteryEnd]);


  useEffect(() => {
    const fetchBackendErrors = async () => {
      const { startDate, endDate } = getDateRange(errorsFilter, customErrorsStart, customErrorsEnd) || {};
      if (!startDate || !endDate) return;
      const groupBy = errorsFilter === "today" ? "hourly" : "daily";

      try {
        const res = await apiClient.get(`/metrics_api_errors`, {
          params: {
            from: startDate.toISOString(),
            to: endDate.toISOString()
          }
        });

        const fullDateMap = generateDateRangeMap(startDate, endDate, groupBy);
        const receivedData = res.data.errors_by_day;

        for (const [dateStr, count] of Object.entries(receivedData)) {
          const key = groupBy === 'hourly'
            ? dateStr.slice(0, 13) + ':00:00'
            : dateStr.slice(0, 10);

          if (fullDateMap[key]) {
            fullDateMap[key].errors_500 = count; // You can adjust based on response shape
          }
        }

        setBackendErrors(Object.values(fullDateMap));
      } catch (err) {
        console.error(err);
      }
    };

    fetchBackendErrors();
  }, [errorsFilter, customErrorsStart, customErrorsEnd]);

  useEffect(() => {
    const fetchAccessAttempts = async () => {
      const { startDate, endDate } = getDateRange(filter, customAccessStart, customAccessEnd) || {};
      if (!startDate || !endDate) return;
      const groupBy = filter === "today" ? "hourly" : "daily";
      try {
        const res = await apiClient.get(`/metrics_device_access`, {
          params: {
            from: startDate.toISOString(),
            to: endDate.toISOString(),
            group_by: groupBy
          }
        });
        const formatted = Object.entries(res.data).map(([key, stats]) => {
          const originalDate = new Date(key);
          const displayDate = groupBy === "hourly"
            ? originalDate.toLocaleString("en-US", { hour: "numeric", day: "numeric", month: "short" })
            : originalDate.toLocaleDateString("en-US", { day: "numeric", month: "short" });
          return {
            originalDate,
            date: displayDate,
            total: stats.total_access_attempts,
            failed: stats.failed_access_attempts
          };
        });
        setAccessAttempts(formatted);
      } catch (error) {
        console.error(error);
      }
    };
    fetchAccessAttempts();
  }, [filter, customAccessStart, customAccessEnd]);

  useEffect(() => {
    const fetchUserInteractions = async () => {
      const { startDate, endDate } = getDateRange(userInteractionFilter, customUserStart, customUserEnd) || {};
      if (!startDate || !endDate) return;
      const res = await apiClient.get(`/metrics_device_actions`, {
        params: {
          from: startDate.toISOString(),
          to: endDate.toISOString()
        }
      });
      const today = new Date();
      const summary = res.data;
      const formatted = [{
        originalDate: today,
        date: today.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        lock: summary.lock_device || 0,
        unlock: summary.unlock_device || 0,
        lights_on: summary.light_on || 0,
        lights_off: summary.light_off || 0
      }];
      setUserInteractions(formatted);
    };
    fetchUserInteractions();
  }, [userInteractionFilter, customUserStart, customUserEnd]);

  const parseInputDate = dateString => {
    if (!dateString) return null;
    const [yyyy, mm, dd] = dateString.split("-");
    const year = parseInt(yyyy, 10);
    const month = parseInt(mm, 10) - 1;
    const day = parseInt(dd, 10);
    const dateObj = new Date(year, month, day);
    if (dateObj.getMonth() !== month || dateObj.getDate() !== day || dateObj.getFullYear() !== year) {
      return null;
    }
    return dateObj;
  };

  const getDateRange = (filterValue, customStart, customEnd) => {
    const now = new Date();
    let startDate;
    let endDate = now;
    if (filterValue === "today") {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (filterValue === "7days") {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
    } else if (filterValue === "30days") {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 30);
    } else if (filterValue === "custom") {
      const parsedStart = parseInputDate(customStart);
      const parsedEnd = parseInputDate(customEnd);
      if (!parsedStart || !parsedEnd || parsedStart > parsedEnd || parsedEnd > now) {
        return null;
      }
      startDate = parsedStart;
      endDate = parsedEnd;
    }
    if (!startDate) return null;
    return { startDate, endDate };
  };

  const getFilteredData = () => ({
    sumTotal: accessAttempts.reduce((acc, a) => acc + a.total, 0),
    sumFailed: accessAttempts.reduce((acc, a) => acc + a.failed, 0)
  });

  const getFilteredBatteryData = () => {
    const { startDate, endDate } = getDateRange(batteryFilter, customBatteryStart, customBatteryEnd) || {};
    if (!startDate || !endDate) return [{ date: 'No Data', avg_battery_pct: 0, peak_usage_pct: 0 }];
    const data = avgBattery.filter(item => item.originalDate >= startDate && item.originalDate <= endDate);
    return data.length > 0 ? data : [{ date: 'No Data', avg_battery_pct: 0, peak_usage_pct: 0 }];
  };

  const getFilteredErrorsData = () => {
    const { startDate, endDate } = getDateRange(errorsFilter, customErrorsStart, customErrorsEnd) || {};
    if (!startDate || !endDate) return [{ date: 'No Data', errors_403: 0, errors_500: 0 }];
    const data = backendErrors.filter(item => item.originalDate >= startDate && item.originalDate <= endDate);
    return data.length > 0 ? data : [{ date: 'No Data', errors_403: 0, errors_500: 0 }];
  };

  const generateDateRangeMap = (startDate, endDate, groupBy) => {
    const dateMap = {};
    const current = new Date(startDate);
    while (current <= endDate) {
      const key = groupBy === 'hourly'
        ? current.toISOString().slice(0, 13) + ':00:00' // hourly key (e.g., 2025-07-08T15:00:00)
        : current.toISOString().slice(0, 10);           // daily key (e.g., 2025-07-08)
      dateMap[key] = {
        originalDate: new Date(current),
        date: groupBy === 'hourly'
          ? current.toLocaleTimeString("en-US", { hour: "2-digit", hour12: true })
          : current.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        errors_403: 0,
        errors_500: 0,
      };
      if (groupBy === 'hourly') current.setHours(current.getHours() + 1);
      else current.setDate(current.getDate() + 1);
    }
    return dateMap;
  };


  const generateBatteryDateRangeMap = (startDate, endDate, groupBy) => {
    const dateMap = {};
    const current = new Date(startDate);
    while (current <= endDate) {
      const key = groupBy === 'hourly'
        ? current.toISOString().slice(0, 13) + ':00:00'
        : current.toISOString().slice(0, 10);

      dateMap[key] = {
        originalDate: new Date(current),
        date: groupBy === 'hourly'
          ? current.toLocaleTimeString("en-US", { hour: "2-digit", hour12: true })
          : current.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        avg_battery_pct: 0,
        peak_usage_pct: 0
      };

      if (groupBy === 'hourly') {
        current.setHours(current.getHours() + 1);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }
    return dateMap;
  };



  const getFilteredUserInteractions = () => {
    const { startDate, endDate } = getDateRange(userInteractionFilter, customUserStart, customUserEnd) || {};
    if (!startDate || !endDate) return [];
    return userInteractions.filter(item => item.originalDate >= startDate && item.originalDate <= endDate);
  };

  const { sumTotal, sumFailed } = getFilteredData();
  const failedPercentage = sumTotal > 0 ? (sumFailed / sumTotal) * 100 : 0;
  const radialData = [
    { name: "Failed Access", value: failedPercentage, fill: "#4B6C87" },
    { name: "Remaining", value: 100 - failedPercentage, fill: "#D3D3D3" }
  ];

  const getUserInteractionSums = () => {
    const filtered = getFilteredUserInteractions();
    return filtered.reduce(
      (acc, item) => ({
        lock: acc.lock + item.lock,
        unlock: acc.unlock + item.unlock,
        lights_on: acc.lights_on + item.lights_on,
        lights_off: acc.lights_off + item.lights_off
      }),
      { lock: 0, unlock: 0, lights_on: 0, lights_off: 0 }
    );
  };

  const getAggregatedFirmwareData = () => firmwareData;

  const userSums = getUserInteractionSums();
  const totalInteractions = userSums.lock + userSums.unlock + userSums.lights_on + userSums.lights_off;

  const formatNumber = num => num > 999 ? (num / 1000).toFixed(1) + " k" : num;

  const isCustomAccessDateInvalid = filter === "custom" && !getDateRange(filter, customAccessStart, customAccessEnd);
  const isCustomBatteryDateInvalid = batteryFilter === "custom" && !getDateRange(batteryFilter, customBatteryStart, customBatteryEnd);
  const isCustomErrorsDateInvalid = errorsFilter === "custom" && !getDateRange(errorsFilter, customErrorsStart, customErrorsEnd);
  const isCustomUserDateInvalid = userInteractionFilter === "custom" && !getDateRange(userInteractionFilter, customUserStart, customUserEnd);

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Hi, {profile ? profile.Name : "User"}. Welcome back to Admin!</p>
      </div>
      <div className="dashboard-content">
        <div className="dashboard-left">
          <div className="dashboard-left-header">
            <p className="dashboard-title">User Interaction with Device</p>
            <select className="dashboard-dropdown" value={userInteractionFilter} onChange={e => setUserInteractionFilter(e.target.value)}>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {userInteractionFilter === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="date" value={customUserStart} onChange={e => setCustomUserStart(e.target.value)} />
                <input type="date" value={customUserEnd} onChange={e => setCustomUserEnd(e.target.value)} />
              </div>
              {isCustomUserDateInvalid && <span style={{ color: "red", fontSize: "0.9rem" }}>Invalid date</span>}
            </div>
          )}
          <h1 className="dashboard-number">{formatNumber(totalInteractions)}</h1>
          <div className="dashboard-progress-bar">
            <div className="progress-section lock"></div>
            <div className="progress-section unlock"></div>
            <div className="progress-section lights-on"></div>
            <div className="progress-section lights-off"></div>
          </div>
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <span className="dot lock"></span> Lock
              <h2>{formatNumber(userSums.lock)}</h2>
            </div>
            <div className="dashboard-card">
              <span className="dot unlock"></span> Unlock
              <h2>{formatNumber(userSums.unlock)}</h2>
            </div>
            <div className="dashboard-card">
              <span className="dot lights-on"></span> Lights On
              <h2>{formatNumber(userSums.lights_on)}</h2>
            </div>
            <div className="dashboard-card">
              <span className="dot lights-off"></span> Lights Off
              <h2>{formatNumber(userSums.lights_off)}</h2>
            </div>
          </div>
        </div>
        <div className="dashboard-right">
          <div className="dashboard-right-header">
            <p className="dashboard-title">Firmware Version</p>
            {firmwareFilter === "custom" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="date" value={customFirmwareStart} onChange={e => setCustomFirmwareStart(e.target.value)} />
                  <input type="date" value={customFirmwareEnd} onChange={e => setCustomFirmwareEnd(e.target.value)} />
                </div>
                {(!parseInputDate(customFirmwareStart) || !parseInputDate(customFirmwareEnd) || parseInputDate(customFirmwareStart) > parseInputDate(customFirmwareEnd)) && (
                  <span style={{ color: "red", fontSize: "0.9rem" }}>Invalid date</span>
                )}
              </div>
            )}
          </div>
          <div className="dashboard-chart-container">
            <CustomFirmwarePieChart data={getAggregatedFirmwareData()} />
          </div>
        </div>
      </div>
      <div className="dashboard-bottom" style={{ display: window.innerWidth > 1400 ? "row" : "column", justifyContent: "space-between", background: "none" }}>
        <div className="dashboard-graph" style={{ flex: 1, margin: "10px", background: "white", boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.1)", padding: "15px", borderRadius: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
            <h3>Access Attempts</h3>
            <div>
              <label htmlFor="filter-select">Filter: </label>
              <select id="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
                <option value="today">Today</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {filter === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="date" value={customAccessStart} onChange={e => setCustomAccessStart(e.target.value)} />
                <input type="date" value={customAccessEnd} onChange={e => setCustomAccessEnd(e.target.value)} />
              </div>
              {isCustomAccessDateInvalid && <span style={{ color: "red", fontSize: "0.9rem" }}>Invalid date</span>}
            </div>
          )}
          <ResponsiveContainer width="100%" height={200}>
            <RadialBarChart cx="50%" cy="60%" innerRadius="80%" outerRadius="100%" startAngle={180} endAngle={0} data={radialData}>
              <RadialBar dataKey="value" clockWise barSize={15} />
            </RadialBarChart>
          </ResponsiveContainer>
          <p style={{ textAlign: "center", fontSize: "14px", fontWeight: "bold", marginTop: "-15px" }}>{failedPercentage.toFixed(1)}%</p>
          <p style={{ textAlign: "center", color: "red", marginTop: "2px" }}>Failed Access</p>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "5px", gap: "15px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#4B6C87", borderRadius: "50%" }}></div>
              <span style={{ fontSize: "12px", color: "#333" }}>Failed %</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#D3D3D3", borderRadius: "50%" }}></div>
              <span style={{ fontSize: "12px", color: "#333" }}>Remaining %</span>
            </div>
          </div>
        </div>
        <div className="dashboard-graph" style={{ flex: 1, margin: "10px", background: "white", boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.1)", padding: "15px", borderRadius: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3>Avg Device Battery</h3>
            <div>
              <label htmlFor="battery-filter-select">Filter: </label>
              <select id="battery-filter-select" value={batteryFilter} onChange={e => setBatteryFilter(e.target.value)}>
                <option value="today">Today</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {batteryFilter === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="date" value={customBatteryStart} onChange={e => setCustomBatteryStart(e.target.value)} />
                <input type="date" value={customBatteryEnd} onChange={e => setCustomBatteryEnd(e.target.value)} />
              </div>
              {isCustomBatteryDateInvalid && <span style={{ color: "red", fontSize: "0.9rem" }}>Invalid date</span>}
            </div>
          )}
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={getFilteredBatteryData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="avg_battery_pct" stroke="#4B6C87" strokeWidth={2} name="Avg Battery" />
              <Line type="monotone" dataKey="peak_usage_pct" stroke="#C792EA" strokeWidth={2} name="Peak Usage" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="dashboard-graph" style={{ flex: 1, margin: "10px", background: "white", boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.1)", padding: "15px", borderRadius: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3>Backend Errors Frequency</h3>
            <div>
              <label htmlFor="errors-filter-select">Filter: </label>
              <select id="errors-filter-select" value={errorsFilter} onChange={e => setErrorsFilter(e.target.value)}>
                <option value="today">Today</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {errorsFilter === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="date" value={customErrorsStart} onChange={e => setCustomErrorsStart(e.target.value)} />
                <input type="date" value={customErrorsEnd} onChange={e => setCustomErrorsEnd(e.target.value)} />
              </div>
              {isCustomErrorsDateInvalid && <span style={{ color: "red", fontSize: "0.9rem" }}>Invalid date</span>}
            </div>
          )}
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={getFilteredErrorsData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="errors_403" stroke="#4B6C87" strokeWidth={2} name="400 Errors" />
              <Line type="monotone" dataKey="errors_500" stroke="#C792EA" strokeWidth={2} name="500 Errors" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
